/**
 * Deep compares two objects or arrays for equality
 * @param obj1 First object to compare
 * @param obj2 Second object to compare
 * @param ignoreKeys Optional array of keys to ignore during comparison
 * @returns boolean indicating if objects are equal
 */
export function objectCompare<T>(
    obj1: T,
    obj2: T,
    ignoreKeys: string[] = [],
): boolean {
    // Handle null/undefined
    if (obj1 === obj2) {
        return true;
    }
    if (!obj1 || !obj2) {
        return false;
    }

    // Handle different types
    if (typeof obj1 !== typeof obj2) {
        return false;
    }

    // Handle arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false;
        }

        // Sort arrays for consistent comparison if they contain objects
        // with an 'id' or 'type' property
        const shouldSort =
            obj1.length > 0 &&
            typeof obj1[0] === 'object' &&
            obj1[0] !== null &&
            ('id' in (obj1[0] as object) || 'type' in (obj1[0] as object));

        if (shouldSort) {
            const sortField = 'id' in (obj1[0] as object) ? 'id' : 'type';
            const sortedObj1 = [...obj1].sort((a: any, b: any) => {
                return String(a[sortField]) < String(b[sortField])
                    ? -1
                    : String(a[sortField]) > String(b[sortField])
                      ? 1
                      : 0;
            });

            const sortedObj2 = [...obj2].sort((a: any, b: any) => {
                return String(a[sortField]) < String(b[sortField])
                    ? -1
                    : String(a[sortField]) > String(b[sortField])
                      ? 1
                      : 0;
            });

            return sortedObj1.every((item: any, index: number) =>
                objectCompare(item, sortedObj2[index], ignoreKeys),
            );
        }

        // Regular array comparison
        return obj1.every((item, index) =>
            objectCompare(item, obj2[index], ignoreKeys),
        );
    }

    // Handle objects
    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
        const keys1 = Object.keys(obj1 as object).filter(
            (key) => !ignoreKeys.includes(key),
        );
        const keys2 = Object.keys(obj2 as object).filter(
            (key) => !ignoreKeys.includes(key),
        );

        if (keys1.length !== keys2.length) {
            return false;
        }

        return keys1.every((key) => {
            // Skip ignored keys
            if (ignoreKeys.includes(key)) {
                return true;
            }

            // If key doesn't exist in second object
            if (!(key in (obj2 as object))) {
                return false;
            }

            const value1 = (obj1 as any)[key];
            const value2 = (obj2 as any)[key];

            // Handle dates - check if objects are Date instances
            if (
                value1 &&
                value2 &&
                Object.prototype.toString.call(value1) === '[object Date]' &&
                Object.prototype.toString.call(value2) === '[object Date]'
            ) {
                return (
                    (value1 as Date).getTime() === (value2 as Date).getTime()
                );
            }

            // Recursively compare nested objects
            if (
                typeof value1 === 'object' &&
                value1 !== null &&
                typeof value2 === 'object' &&
                value2 !== null
            ) {
                return objectCompare(value1, value2, ignoreKeys);
            }

            // Direct comparison for primitives
            return value1 === value2;
        });
    }

    // Handle primitives
    return obj1 === obj2;
}
